import { useEffect, useMemo } from "react";
import { useMatch, useNavigate } from "react-router";
import { SpinnerLegacy } from "../../components";
import { useDashboardContext } from "../../components/dashboard/Dashboard.shared";
import { InfiniteList } from "../../components/infiniteLoading/InfiniteList";
import {
  NavigationBarMenu,
  NavigationBarSubHeader,
} from "../../components/navigation/NavigationBar";
import { NavigationTabGroup } from "../../components/navigationTabs/NavigationTabsGroup";
import { NavigationTabList } from "../../components/navigationTabs/NavigationTabList";
import { NavigationTabPanels } from "../../components/navigationTabs/NavigationTabPanels";
import {
  DISTRIBUTION_DETAILS_ALLOCATED,
  DISTRIBUTION_DETAILS_PROCESSING,
} from "../../constants/routes";
import { useParamAsNumber } from "../../hooks/useParamAsNumber";
import { useSellarDistributionCollectionList } from "../../hooks/api/v2/sellarDistributionCollection/useSellarDistributionCollectionList";
import dayjs from "dayjs";
import { DistributionCollectionListItem } from "./DistributionCollectionListItem";

export const DistributionCollectionsList = () => {
  const tabs = ["Processing", "Allocated"];

  return (
    <NavigationTabGroup
      tabs={tabs}
      panelFactory={(tab) => {
        switch (tab) {
          case "Processing":
            return <Processing />;
          case "Allocated":
            return <Allocated />;
          default:
            return null;
        }
      }}
    >
      <NavigationBarMenu title={"Collections"} />
      <NavigationBarSubHeader>
        <div className="h-full bg-gray-100">
          <NavigationTabList />
          <NavigationTabPanels />
        </div>
      </NavigationBarSubHeader>
    </NavigationTabGroup>
  );
};

const Processing = () => {
  const { isTwoPanel } = useDashboardContext();
  const taskDate = useMatch(DISTRIBUTION_DETAILS_PROCESSING.pattern)?.params
    .taskDate;
  const navigate = useNavigate();

  const collectionListQuery = useSellarDistributionCollectionList({
    limit: 10,
    awaitingConfirmation: true,
  });

  const collections = useMemo(
    () => collectionListQuery.data?.pages.flatMap((page) => page.data) ?? [],
    [collectionListQuery.data?.pages],
  );

  useEffect(() => {
    if (isTwoPanel && !taskDate && collections.length > 0) {
      const firstCollection = collections[0];
      if (!firstCollection) return;

      navigate(
        DISTRIBUTION_DETAILS_PROCESSING.href({
          taskDate: dayjs(firstCollection.date).format("YYYY-MM-DD"),
        }),
        {
          replace: true,
        },
      );
    }
  }, [isTwoPanel, taskDate, navigate, collections]);

  if (collectionListQuery.isLoading) return <SpinnerLegacy />;

  return (
    <InfiniteList query={collectionListQuery}>
      {collections.map((collection) => (
        <DistributionCollectionListItem
          key={dayjs(collection.date).format("YYYY-MM-DD")}
          collection={collection}
          type="processing"
        />
      ))}
    </InfiniteList>
  );
};

const Allocated = () => {
  const { isTwoPanel } = useDashboardContext();
  const taskId = useParamAsNumber(
    DISTRIBUTION_DETAILS_ALLOCATED,
    "taskId",
    false,
  );
  const navigate = useNavigate();

  const collectionListQuery = useSellarDistributionCollectionList({
    limit: 10,
  });

  const collections = useMemo(
    () => collectionListQuery.data?.pages.flatMap((page) => page.data) ?? [],
    [collectionListQuery.data?.pages],
  );

  useEffect(() => {
    if (isTwoPanel && !taskId && collections.length > 0) {
      const firstCollection = collections[0];
      if (!firstCollection) return;
      if (firstCollection.id === undefined) return;

      navigate(
        DISTRIBUTION_DETAILS_ALLOCATED.href({ taskId: firstCollection.id }),
        { replace: true },
      );
    }
  }, [isTwoPanel, taskId, navigate, collections]);

  if (collectionListQuery.isLoading) return <SpinnerLegacy />;

  return (
    <InfiniteList query={collectionListQuery}>
      {collections.map((collection) => (
        <DistributionCollectionListItem
          key={dayjs(collection.date).format("YYYY-MM-DD")}
          collection={collection}
          type="allocated"
        />
      ))}
    </InfiniteList>
  );
};
